import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import SmallPopupTemplate from "components/templates/modal/small/SmallPopupTemplate";
import { useMemo, useState } from "react";
import AddHealth from "./health";
import AddCvv from "./cvv";
import AddInstall from "./installation";
import AddInventory from "./inventory";
import AddThumbnail from "./thumbnail";
import AddTimelapse from "./timelapse";
import AddCpReport from "./cp";
import {
  CPReport,
  CPReportDataRow,
  CVVReport,
  HealthReport,
  InstallReport,
  InventoryReport,
  ThumbnailReport,
} from "api/interfaces/reportInterface.interface";
import { ReportDataRow } from "api/interfaces/reportInterface.interface";
import AddReportItem, { AddListType } from "./AddReportItem";
import { changeTypeNumberForReportText } from "utils/functions";
import {
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useAppSelector } from "redux/hooks";
import { MdHealthAndSafety } from "react-icons/md";
import { BiSolidCctv } from "react-icons/bi";
import { MdInventory } from "react-icons/md";
import { IoImages } from "react-icons/io5";
import { CgTimelapse } from "react-icons/cg";
import BigButton from "components/atoms/buttons/BigButton";
import { FaTools } from "react-icons/fa";
import { RadioWrapper } from "./AddReportStyled";

export type AddReportProps = {
  onModal: (type?: string) => void;
  isEdit?: boolean;
  reportRow?: ReportDataRow | CPReportDataRow;
  data?:
    | HealthReport
    | InventoryReport
    | InstallReport
    | CVVReport
    | ThumbnailReport
    | CPReport;
  onSuccessDelete?: () => void;
};

export type AddReportListType =
  | "health"
  | "cvv"
  | "inventory"
  | "installation"
  | "thumbnail"
  | "timelapse";
export default function AddReport(props: AddReportProps): JSX.Element {
  // console.log(props.reportRow);
  type ObjType = {
    [key in AddReportListType]: React.ReactNode;
  };

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  // next 버튼 클릭 시 true 로 전환 (이 방식이 맞는 지 모르겠음. by.ydoni)
  const [isAdd, setIsAdd] = useState<boolean>(props.isEdit ? true : false);

  const [checkedType, setCheckedType] = useState<AddReportListType>(
    props.isEdit && props.reportRow !== undefined
      ? changeTypeNumberForReportText(props.reportRow.type)
      : "health"
  );

  const addType: ObjType = {
    health:
      selectedAccount.accountLevel === LEVEL_TYPE.EU ? (
        <AddHealth
          {...props}
          reportRow={props.reportRow as ReportDataRow}
          data={props.data as HealthReport}
        />
      ) : (
        <AddCpReport
          {...props}
          reportRow={props.reportRow as CPReportDataRow}
          data={props.data as CPReport}
        />
      ),

    cvv: (
      <AddCvv
        {...props}
        reportRow={props.reportRow as ReportDataRow}
        data={props.data as CVVReport}
      />
    ),

    inventory: (
      <AddInventory
        {...props}
        reportRow={props.reportRow as ReportDataRow}
        data={props.data as InventoryReport}
      />
    ),

    installation: (
      <AddInstall
        {...props}
        reportRow={props.reportRow as ReportDataRow}
        data={props.data as InstallReport}
      />
    ),

    thumbnail: (
      <AddThumbnail
        {...props}
        reportRow={props.reportRow as ReportDataRow}
        data={props.data as ThumbnailReport}
      />
    ),

    timelapse: <AddTimelapse onModal={props.onModal} />,
  };

  const euAddList: AddListType[] = [
    {
      title: "Health",
      subTitle: "Provides a usage summary of one or more sites.",
      value: "health",
      disabled: false,
      icon: <MdHealthAndSafety />,
    },
    {
      title: "CVV (Camera View Verification)",
      subTitle: "Uses CVV to inform you of any scenery changes.",
      value: "cvv",
      disabled: false,
      icon: <BiSolidCctv />,
    },
    {
      title: "Inventory",
      subTitle: "Displays a list of sites with detailed information.",
      value: "inventory",
      disabled: false,
      icon: <MdInventory />,
    },
    // {
    //   title: "Installation",
    //   subTitle:
    //     "Provides installation confirmation with recorder and camera information.",
    //   value: "installation",
    //   disabled: false,
    //   icon: <FaTools />,
    // },
    {
      title: "Thumbnail",
      subTitle: "Save thumbnails periodically.",
      value: "thumbnail",
      disabled: true,
      icon: <IoImages />,
    },
    {
      title: "Timelapse",
      subTitle:
        "Create timelapses at a set duration and speed for any of your camera channels.",
      value: "timelapse",
      disabled: true,
      icon: <CgTimelapse />,
    },
  ];

  const cpAddList: AddListType[] = [
    {
      title: "Health & Inventory",
      subTitle: "Deisplays a list of sites with detailed information.",
      value: "health",
      disabled: false,
      icon: <MdHealthAndSafety />,
    },
    {
      title: "Installation",
      subTitle:
        "Provides installation confirmation with recorder and camera information.",
      value: "installation",
      disabled: false,
      icon: <FaTools />,
    },
  ];

  const onChangeType = (value: AddReportListType) => {
    setCheckedType(value);
  };

  const reportAddList = useMemo(() => {
    return selectedAccount.accountLevel === LEVEL_TYPE.EU
      ? euAddList
      : cpAddList;
  }, [selectedAccount.accountLevel]);

  return props.isEdit || isAdd ? (
    <BigPopupTemplate
      title={`${props.isEdit ? "Edit" : "Add"} ${
        reportAddList.find((add) => add.value === checkedType)?.title
      } Report`}
      onModal={props.onModal}
    >
      {addType[checkedType]}
    </BigPopupTemplate>
  ) : (
    <SmallPopupTemplate
      title="Create a New Report"
      subTitle="Select a type of report"
      onClick={() => setIsAdd(true)}
      onModal={props.onModal}
      buttons={
        <BigButton
          label="Next"
          onClickBtn={() => setIsAdd(true)}
          data-testid="reports-add-add-button-set-is-add"
        />
      }
      className="add-report-popup"
      data-testid="reports-add-add-small-popup-template-set-is-add"
    >
      <RadioWrapper level={selectedAccount.accountLevel}>
        {reportAddList.map((item, index) => (
          <AddReportItem
            level={selectedAccount.accountLevel}
            item={item}
            index={index}
            onChangeType={onChangeType}
            checkedType={checkedType}
          />
        ))}
      </RadioWrapper>
    </SmallPopupTemplate>
  );
}
